/**
 * Harmony SDK Configuration
 * Supports both local development and remote modes
 */

import { client as tenantsClient } from "@harmonyso/tenants-sdk";
import { client as usersClient } from "@harmonyso/users-sdk";
import { client as authClient } from "@harmonyso/auth-sdk";
import { client as conversationsViewerClient } from "@harmonyso/conversations-viewer-sdk";
import { client as insightsEngineClient } from "@harmonyso/insights-engine-sdk";
import { client as billingClient } from "@harmonyso/billing-api-sdk";
import { getSessionToken } from "@descope/react-sdk";
import { AppMode } from "@/lib/config/types";
import { configureBootstrapClient } from "@/lib/api/bootstrap-client";
import { configureIntegrationsClient } from "@/lib/api/integrations-client";
import { configureServiceDeskClient } from "@/lib/api/service-desk-client";
import { configureServiceCatalogClient } from "@/lib/api/service-catalog-client";
import { configureBackofficeClient } from "@/lib/api/backoffice-client";
import { getStoredConfig } from "@/lib/config/stored-config";
import { remoteCellForBaseUrl } from "@/lib/cells/cells";
import { getCellSessionToken } from "@/lib/cells/cell-session";
import { routeToTenantCell } from "@/lib/cells/route-to-tenant-cell";
import { TenantCellUnknownError } from "@/lib/cells/tenant-cells";

export const CONVERSATIONS_VIEWER_SERVICE_PATH = "/conversations-viewer";
export const INSIGHTS_ENGINE_SERVICE_PATH =
  "/backoffice-api/api/v1/proxy/insights";

type SDKClient = typeof tenantsClient;

// Configure SDK based on current mode
const configureSDKClient = (
  client: SDKClient,
  serviceName: string,
  baseUrlKey: string,
  defaultUrl: string,
  // Service path on another cell's API, for SDKs whose requests name a
  // tenant in `tenant_id`: those go to the cell the tenant lives in.
  tenantCellServicePath?: string,
) => {
  try {
    const config = getStoredConfig();
    const mode = config?.mode ?? AppMode.REMOTE;

    let baseUrl: string;
    if (mode === AppMode.LOCAL) {
      const localBaseUrl = config?.localBaseUrl ?? "http://localhost:8000";
      baseUrl = `${localBaseUrl}/api/v1`;
    } else {
      const serviceBaseUrl = (config && Object.prototype.hasOwnProperty.call(config, baseUrlKey))
        ? (config[baseUrlKey as keyof typeof config] as string | undefined)
        : undefined;
      baseUrl = serviceBaseUrl ?? defaultUrl;
    }

    client.instance.defaults.baseURL = baseUrl;
    client.instance.interceptors.request.use(async (v) => {
      if (mode === AppMode.REMOTE && tenantCellServicePath) {
        try {
          if (await routeToTenantCell(v, tenantCellServicePath)) {
            return v;
          }
        } catch (error) {
          // A tenant no cell lists (e.g. deleted, with data left behind) is
          // read where the request already points, as before cell routing.
          if (!(error instanceof TenantCellUnknownError && error.definite)) {
            throw error;
          }
        }
      }

      const remoteCell =
        mode === AppMode.REMOTE ? remoteCellForBaseUrl(v.baseURL) : undefined;
      if (remoteCell) {
        v.headers.set(
          "Authorization",
          `Bearer ${await getCellSessionToken(remoteCell)}`,
        );
        return v;
      }

      try {
        if (mode === AppMode.REMOTE) {
          // Remote mode: use JWT Bearer token
          const token = getSessionToken();
          if (token) {
            v.headers.set("Authorization", `Bearer ${token}`);
          }
        } else {
          // Local mode: use x-tenant-id header instead of auth
          const localTenantId = config?.localTenantId;
          if (localTenantId) {
            v.headers.set("x-tenant-id", localTenantId);
          }
        }

        // For debugging: always add force tenant ID if set in env
        if (import.meta.env.DEV && import.meta.env.VITE_FORCE_TENANT_ID) {
          v.headers.set("x-tenant-id", import.meta.env.VITE_FORCE_TENANT_ID);
        }
      } catch (error: unknown) {
        console.error(
          `Failed to configure ${serviceName} request headers:`,
          error,
        );
      }
      return v;
    });

    console.log(
      `${serviceName} SDK configured for ${mode} mode with baseURL: ${baseUrl}`,
    );
  } catch (error) {
    console.error(`Failed to configure ${serviceName} SDK:`, error);
  }
};

export const configureTenantsSDK = (): void => {
  configureSDKClient(
    tenantsClient,
    "Tenants",
    "tenantsBaseUrl",
    `${import.meta.env.VITE_API_BASE_URL || "https://api.dev.us1.harmony.so"}/tenants`,
  );
};

export const configureUsersSDK = (): void => {
  configureSDKClient(
    usersClient,
    "Users",
    "usersBaseUrl",
    `${import.meta.env.VITE_API_BASE_URL || "https://api.dev.us1.harmony.so"}/users`,
  );
};

export const configureAuthSDK = (): void => {
  const defaultUrl = import.meta.env.DEV
    ? "/auth"
    : `${import.meta.env.VITE_API_BASE_URL || "https://api.dev.us1.harmony.so"}/auth`;
  configureSDKClient(
    authClient,
    "Auth",
    "authBaseUrl",
    defaultUrl,
  );
};

export const configureConversationsViewerSDK = (): void => {
  configureSDKClient(
    conversationsViewerClient,
    "Conversations Viewer",
    "conversationsViewerBaseUrl",
    `${import.meta.env.VITE_API_BASE_URL || "https://api.dev.us1.harmony.so"}${CONVERSATIONS_VIEWER_SERVICE_PATH}`,
    CONVERSATIONS_VIEWER_SERVICE_PATH,
  );
};

export const configureInsightsEngineSDK = (): void => {
  configureSDKClient(
    insightsEngineClient,
    "Insights Engine",
    "insightsEngineBaseUrl",
    `${import.meta.env.VITE_API_BASE_URL || "https://api.dev.us1.harmony.so"}${INSIGHTS_ENGINE_SERVICE_PATH}`,
    INSIGHTS_ENGINE_SERVICE_PATH,
  );
};

export const configureBillingSDK = (): void => {
  configureSDKClient(
    billingClient,
    "Billing",
    "billingBaseUrl",
    `${import.meta.env.VITE_API_BASE_URL || "https://api.dev.us1.harmony.so"}/billing`,
  );
};

export const configureAllSDKs = (): void => {
  configureTenantsSDK();
  configureUsersSDK();
  configureAuthSDK();
  configureConversationsViewerSDK();
  configureInsightsEngineSDK();
  configureBillingSDK();
  configureBootstrapClient();
  configureIntegrationsClient();
  configureServiceDeskClient();
  configureServiceCatalogClient();
  configureBackofficeClient();
};

// Initialize SDKs with default configuration at module load time
// This prevents requests from going to localhost before the SDK is configured
configureAllSDKs();
